import React from 'react';

export interface JourneyTab {
  id: string;
  emoji: string;
  label: string;
}

export interface JourneyTabsProps {
  tabs: JourneyTab[];
  activeTabId: string | null;
  lastActedTabId: string | null;
  onTabClick: (tabId: string) => void;
}

/**
 * Horizontal tab strip for the workshop journey. Each tab shows
 * { emoji label } and TWO independent markers:
 *   ✱ (amber) when its id === lastActedTabId — history badge
 *   • (emerald) when its id === activeTabId — present badge
 * Distinct colors + separate spans so the eye can read both signals
 * (per plan-triad ux #2: same-color same-span produces visual mush).
 *
 * Clicking the active tab is a no-op (no self-toggle — explicit Esc
 * retracts elsewhere).
 *
 * Pure presentational. Tailwind `dark:` variants throughout per
 * `feedback_dark_mode_parity_first_class`. ComposePanel owns
 * activeTabId + lastActedTabId.
 */
export const JourneyTabs: React.FC<JourneyTabsProps> = ({
  tabs,
  activeTabId,
  lastActedTabId,
  onTabClick,
}) => {
  const handleClick = (tabId: string) => {
    if (tabId === activeTabId) return;
    onTabClick(tabId);
  };

  return (
    <div
      role="tablist"
      aria-label="Workshop journey tabs"
      className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-gray-900/40"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        const isLastActed = tab.id === lastActedTabId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            data-testid={`journey-tab-${tab.id}`}
            aria-selected={isActive}
            onClick={() => handleClick(tab.id)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-sm font-medium transition-colors ${
              isActive
                ? 'bg-amber-200/70 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200'
                : 'text-gray-700 hover:bg-amber-100/60 dark:text-gray-300 dark:hover:bg-gray-700/40'
            }`}
          >
            <span aria-hidden>{tab.emoji}</span>
            <span>{tab.label}</span>
            {isLastActed && (
              <span
                aria-hidden
                data-testid="journey-tab-star"
                className="ml-0.5 leading-none text-amber-500 dark:text-amber-400"
              >
                ✱
              </span>
            )}
            {isActive && (
              <span
                aria-hidden
                data-testid="journey-tab-dot"
                className="leading-none font-bold text-emerald-500 dark:text-emerald-400"
              >
                •
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
